/**
 * Place postcard — the diary theme's animated stand-in for the Google map
 * iframe on a place card. The embed can't be themed; this can. A tiny
 * living scene (sun, drifting clouds, a landmark swaying under a bobbing
 * pin) tops a postcard with a postage stamp and a typed postmark.
 *
 * The scene flavor is a cosmetic guess from tags/title keywords and falls
 * back to a cheerful generic meadow — it decorates, it never gates data.
 * Purely visual: callers branch on isDiary and keep their modern card.
 */
import React from 'react'

type SceneKind =
  | 'home' | 'work' | 'nature' | 'fitness' | 'school'
  | 'food' | 'shop' | 'health' | 'generic'

const SCENES: Record<SceneKind, { landmark: string; keywords: string[] }> = {
  home:    { landmark: '🏡', keywords: ['home', 'house', 'apartment', 'casa'] },
  work:    { landmark: '🏢', keywords: ['work', 'office', 'oficina'] },
  nature:  { landmark: '🌳', keywords: ['park', 'trail', 'nature', 'garden', 'lake', 'beach', 'hike'] },
  fitness: { landmark: '🤸', keywords: ['gym', 'fitness', 'yoga', 'pool', 'court', 'field', 'run'] },
  school:  { landmark: '🏫', keywords: ['school', 'college', 'university', 'campus', 'library', 'class'] },
  food:    { landmark: '☕', keywords: ['cafe', 'coffee', 'restaurant', 'food', 'lunch', 'dinner', 'bakery'] },
  shop:    { landmark: '🛒', keywords: ['store', 'shop', 'market', 'grocery', 'groceries', 'mall', 'errand'] },
  health:  { landmark: '🩺', keywords: ['doctor', 'dentist', 'clinic', 'hospital', 'therapy', 'health', 'pharmacy'] },
  generic: { landmark: '🌻', keywords: [] },
}

function sceneFor(title: string, tags: string[]): SceneKind {
  const haystack = [title, ...tags].join(' ').toLowerCase()
  for (const [kind, def] of Object.entries(SCENES)) {
    if (kind === 'generic') continue
    if (def.keywords.some((k) => haystack.includes(k))) return kind as SceneKind
  }
  return 'generic'
}

export interface PlacePostcardProps {
  title: string
  address?: string | null
  tags?: string[]
  latitude: number
  longitude: number
  radiusM?: number
  /** Top-right corner content: edit/delete buttons. */
  corner?: React.ReactNode
  footer?: React.ReactNode
  className?: string
}

export const PlacePostcard: React.FC<PlacePostcardProps> = ({
  title,
  address,
  tags = [],
  latitude,
  longitude,
  radiusM,
  corner,
  footer,
  className = '',
}) => {
  const kind = sceneFor(title, tags)
  const { landmark } = SCENES[kind]

  return (
    <div className={`yn-postcard ${className}`}>
      <div className={`yn-scene yn-scene-${kind}`} aria-hidden>
        <span className="yn-sun">☀️</span>
        <span className="yn-cloud" style={{ top: 8 }}>☁️</span>
        <span className="yn-cloud yn-cloud-2">☁️</span>
        <span className="yn-scene-landmark">{landmark}</span>
        <span className="yn-scene-pin">📍</span>
        <span className="yn-scene-pin-shadow" />
        <span className="yn-postcard-stamp">{(title.trim()[0] || '?').toUpperCase()}</span>
      </div>

      <div className="p-4">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="yn-postcard-title truncate">{title}</div>
            {address && <div className="yn-postcard-address truncate mt-0.5">{address}</div>}
          </div>
          {corner}
        </div>

        <div className="yn-postmark mt-2">
          ⌖ {latitude.toFixed(4)}, {longitude.toFixed(4)}
          {typeof radiusM === 'number' ? ` · within ${radiusM}m` : ''}
        </div>

        {tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span key={t} className="yn-postcard-chip">{t}</span>
            ))}
          </div>
        )}

        {footer && <div className="mt-3">{footer}</div>}
      </div>
    </div>
  )
}

export default PlacePostcard
